import { ChevronDown, Loader2, RefreshCw, Sparkles, Undo2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { discussoesControllerMelhorarTexto } from '@/api/generated/discussões-técnicas/discussões-técnicas';
import { ApiError } from '@/api/http-client';
import { diffDePalavras } from '@/features/discussoes/diffDePalavras';
import { CLASSE_CONTROLE } from '@/shared/components/Campo';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible';
import { Textarea } from '@/shared/ui/textarea';
import { toast } from '@/shared/ui/sonner';

interface Props {
  discussaoId: string;
  /** O rascunho do composer no momento do clique — é sempre dele que a IA parte, inclusive no "Gerar de novo". */
  original: string;
  /** O texto (já com as edições do usuário) volta para o composer. NÃO envia. */
  aoUsar: (texto: string) => void;
  aoDescartar: () => void;
  /** 503: o ambiente não tem IA configurada — a tela esconde o botão de vez. */
  aoIndisponivel: () => void;
}

/**
 * Painel "Sugestão da IA" acima do composer do chat.
 *
 * A revisão NUNCA vai direto para o composer nem é enviada: cai num campo
 * editável, o médico/advisor ajusta o que quiser e só então decide usar. O
 * destaque do que a IA mudou compara o rascunho com a sugestão ORIGINAL da IA
 * — depois que o usuário edita, esse diff deixa de descrever o campo, e por
 * isso some (fica o "Restaurar sugestão da IA").
 */
export function SugestaoDeTexto({ discussaoId, original, aoUsar, aoDescartar, aoIndisponivel }: Props) {
  const [sugestao, setSugestao] = useState<string | null>(null);
  const [editado, setEditado] = useState('');
  const [confirmandoGerarDeNovo, setConfirmandoGerarDeNovo] = useState(false);
  const campoRef = useRef<HTMLTextAreaElement>(null);

  const [gerando, setGerando] = useState(false);

  /**
   * Chamada direta, com o "carregando" em estado próprio — e NÃO o
   * `useMutation`: ele é disparado de um efeito de montagem, e no StrictMode
   * (dev) a desmontagem simulada tira o observer da mutation em andamento; o
   * `onSuccess` ainda rodava, mas o `isPending` ficava preso em `true` e o
   * painel inteiro travado (campo e "Usar texto" desabilitados para sempre).
   */
  async function gerar() {
    setGerando(true);
    try {
      const { texto } = await discussoesControllerMelhorarTexto(discussaoId, { texto: original });
      setSugestao(texto);
      setEditado(texto);
      requestAnimationFrame(() => campoRef.current?.focus());
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 503) {
        toast.error('A revisão por IA não está disponível neste ambiente.');
        aoIndisponivel();
        aoDescartar();
        return;
      }
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível melhorar o texto agora.');
      // Sem sugestão nenhuma ainda, o painel não tem o que mostrar. Com uma
      // anterior (falhou o "Gerar de novo"), ela continua valendo.
      if (sugestao === null) aoDescartar();
    } finally {
      setGerando(false);
    }
  }

  // Uma vez por abertura do painel — o pai remonta o componente a cada clique
  // no botão. A trava existe pelo StrictMode: em dev ele roda o efeito duas
  // vezes (o ref sobrevive à remontagem simulada), e seriam duas chamadas pagas.
  const jaPediu = useRef(false);
  useEffect(() => {
    if (jaPediu.current) return;
    jaPediu.current = true;
    void gerar();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só na abertura; `original` é fixo por montagem
  }, []);

  const foiEditado = sugestao !== null && editado !== sugestao;
  const semMudancas = sugestao !== null && sugestao === original;
  const diff = useMemo(() => (sugestao === null ? [] : diffDePalavras(original, sugestao)), [original, sugestao]);

  function gerarDeNovo() {
    setConfirmandoGerarDeNovo(false);
    void gerar();
  }

  return (
    <div
      role="region"
      aria-label="Sugestão da IA"
      className="rounded-lg border border-brand/30 bg-brand/5 p-3 space-y-2"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !confirmandoGerarDeNovo) {
          e.stopPropagation();
          aoDescartar();
        }
      }}
    >
      <div className="flex items-center gap-1.5">
        <Sparkles className="w-4 h-4 text-brand" />
        <span className="text-sm font-semibold text-brand">Sugestão da IA</span>
        <button
          type="button"
          onClick={aoDescartar}
          aria-label="Fechar sugestão"
          className="ml-auto text-muted-foreground hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {sugestao === null ? (
        <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Revisando o texto...
        </div>
      ) : (
        <>
          <Textarea
            ref={campoRef}
            value={editado}
            onChange={(e) => setEditado(e.target.value)}
            aria-label="Texto sugerido pela IA (editável)"
            rows={Math.min(8, Math.max(3, editado.split('\n').length + 1))}
            disabled={gerando}
            className={CLASSE_CONTROLE}
          />

          {semMudancas ? (
            <p className="text-xs text-muted-foreground">A IA não encontrou nada para mudar neste texto.</p>
          ) : foiEditado ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Você editou a sugestão.</span>
              <button
                type="button"
                onClick={() => setEditado(sugestao)}
                className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
              >
                <Undo2 className="w-3 h-3" />
                Restaurar sugestão da IA
              </button>
            </div>
          ) : (
            <div className="text-xs">
              <p className="mb-1 font-medium text-muted-foreground">O que a IA mudou:</p>
              <p data-testid="diff-da-sugestao" className="whitespace-pre-wrap text-foreground">
                {diff.map((trecho, indice) =>
                  trecho.tipo === 'igual' ? (
                    <span key={indice}>{trecho.texto}</span>
                  ) : trecho.tipo === 'removido' ? (
                    <del key={indice} className="bg-destructive/10 text-destructive line-through">
                      {trecho.texto}
                    </del>
                  ) : (
                    <ins key={indice} className="bg-emerald-500/15 text-emerald-700 no-underline dark:text-emerald-400">
                      {trecho.texto}
                    </ins>
                  ),
                )}
              </p>
            </div>
          )}

          <Collapsible>
            <CollapsibleTrigger className="group inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">
              <ChevronDown className="w-3 h-3 transition-transform group-data-[state=open]:rotate-180" />
              Ver texto original
            </CollapsibleTrigger>
            <CollapsibleContent>
              <p className="mt-1 rounded-md bg-muted/50 px-2.5 py-1.5 text-xs text-muted-foreground whitespace-pre-wrap">
                {original}
              </p>
            </CollapsibleContent>
          </Collapsible>

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" size="sm" onClick={aoDescartar}>
              Descartar
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={gerando}
              onClick={() => (foiEditado ? setConfirmandoGerarDeNovo(true) : gerarDeNovo())}
            >
              {gerando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Gerar de novo
            </Button>
            <Button type="button" size="sm" disabled={gerando || !editado.trim()} onClick={() => aoUsar(editado)}>
              Usar texto
            </Button>
          </div>
        </>
      )}

      <AlertDialog open={confirmandoGerarDeNovo} onOpenChange={setConfirmandoGerarDeNovo}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gerar outra sugestão?</AlertDialogTitle>
            <AlertDialogDescription>
              As alterações que você fez na sugestão atual serão perdidas. A IA parte de novo do seu texto original.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Manter minhas alterações</AlertDialogCancel>
            <AlertDialogAction onClick={gerarDeNovo}>Gerar de novo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
